@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.pdf

import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicActivity
import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicDetail
import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicEventStats
import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicEvents
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt

// Zone stats of one calendar month: zone1 = 0-1 km, zone2 = 1-2 km
data class KeraunicMonthlyZone(
    val month: Int,
    val zone1: ApiKeraunicEvents,
    val zone2: ApiKeraunicEvents,
)

/** Top impact of a project as returned by /management/probes/densities, already located (distance in km). */
data class KeraunicTopInput(
    val externalId: String,
    // dd/MM/yyyy in the device time zone, localized by KeraunicReportText.date()
    val dateLabel: String,
    val atSecondsUtc: Long,
    val distKm: Double,
    val latitude: Double,
    val longitude: Double,
    val peakAmp: Int,
    val sensorCount: Int,
    val percent: Double,
)

/** Everything loaded for one report, before any computation. */
data class KeraunicReportInput(
    val latitude: Double,
    val longitude: Double,
    val fromSecondsUtc: Long,
    val toSecondsUtc: Long,
    // "dd/MM/yyyy — dd/MM/yyyy" in the device time zone, localized by KeraunicReportText.period()
    val periodLabel: String,
    val nsg: Double,
    val nsgRefs: List<KeraunicNsgRef> = emptyList(),
    val detail: ApiKeraunicDetail? = null,
    // Counts of /management/probes/densities, only for a LPS Manager project (null for a free point)
    val densityLess1Km: Int? = null,
    val densityLess2Km: Int? = null,
    val topImpacts: List<KeraunicTopInput> = emptyList(),
    val monthlyZones: List<KeraunicMonthlyZone> = emptyList(),
    // UTC offset (minutes) of the device time zone at a given UTC time, for the impact times
    val utcOffsetMinutes: (Long) -> Int = { 0 },
)

/** Calendar time in the device time zone, with its UTC offset (e.g. +120 for UTC+2). */
data class KeraunicLocalTime(
    val year: Int,
    val month: Int,
    val day: Int,
    val hour: Int,
    val minute: Int,
    val offsetMinutes: Int,
)

/** One key fact of the cover: intensity, distance and time of an impact (null when unknown). */
data class KeraunicHighlight(
    val amplitudeKa: Double,
    val isPositive: Boolean?,
    val distKm: Double?,
    val time: KeraunicLocalTime?,
)

/** Key facts of the period: the most intense and the nearest impacts within 2 km. */
data class KeraunicHighlights(
    val strongest: KeraunicHighlight?,
    val nearest: KeraunicHighlight?,
    // No cloud-to-ground strike within 2 km over the period (shown instead of the two lines)
    val noGroundImpact: Boolean = false,
)

enum class KeraunicNsgLevel { LOW, MODERATE, HIGH }

/**
 * Single computed content of the Keraunic Report: the screen and the PDF both render this object,
 * so they always show the same figures.
 */
data class KeraunicReportData(
    val latitude: Double,
    val longitude: Double,
    val fromSecondsUtc: Long,
    val toSecondsUtc: Long,
    val periodLabel: String,
    val nsg: Double,
    val nsgLevel: KeraunicNsgLevel,
    val nsgRefs: List<KeraunicNsgRef>,
    val zone1: KeraunicZoneStats,
    val zone2: KeraunicZoneStats,
    val totalImpacts: Int,
    val maxKa: Double,
    val risk: KeraunicRisk,
    // Sorted by impact probability (percent), highest first
    val topImpacts: List<KeraunicTopImpact>,
    // Nearest, strongest and weakest impacts, used when there is no top impact (free point)
    val fallbackImpacts: List<KeraunicTopImpact>,
    // 12 entries (January to December)
    val monthly1Km: List<KeraunicMonthlyData>,
    val monthly2Km: List<KeraunicMonthlyData>,
    // Hours with at least one impact, both zones combined, sorted by hour (UTC)
    val hourly: List<KeraunicHourlyData>,
    val mapImpacts: List<SvgMapBuilder.ImpactDot>,
    val highlights: KeraunicHighlights = KeraunicHighlights(null, null),
) {
    /** Top impacts listed on the overview (screen and PDF). */
    val shownTopImpacts: List<KeraunicTopImpact> get() = topImpacts.take(SHOWN_TOP_IMPACTS)

    /** Impacts of the top really located in the direct danger zone (key points). */
    val directTopImpacts: List<KeraunicTopImpact> get() = topImpacts.filter { it.distKm < 1.0 }

    companion object {
        const val SHOWN_TOP_IMPACTS = 5
    }
}

internal object KeraunicReportCalculator {
    private const val TOP_IMPACTS_KEPT = 10
    private const val MAP_RADIUS_M = 2000.0
    private const val M_PER_DEG = 111320.0
    private const val EARTH_RADIUS_KM = 6371.0
    private const val SECONDS_PER_DAY = 86_400L
    private const val SAME_PLACE_KM = 0.05
    private const val SAME_INTENSITY_KA = 0.5

    // English short month labels, translated by KeraunicReportText.month()
    private val MONTH_KEYS = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

    fun compute(input: KeraunicReportInput): KeraunicReportData {
        val detail = input.detail
        // Dated flashes of the detailed report when it lists all of them: every figure is then computed from them
        val flashes = completeFlashes(input)
        // Totals come from the detailed report (sum of both zones); densities only when it is missing
        val zone1 = flashes?.let { list -> flashStats(list.filter { it.distKm < 1.0 }) }
            ?: detail?.eventsAt1Km?.let(::zoneStats) ?: KeraunicZoneStats(totalCount = input.densityLess1Km ?: 0)
        val zone2 = flashes?.let { list -> flashStats(list.filter { it.distKm >= 1.0 }) }
            ?: detail?.eventsAt2Km?.let(::zoneStats) ?: KeraunicZoneStats(totalCount = input.densityLess2Km ?: 0)
        val maxKa = KeraunicReportLogic.overallMaxKa(zone1, zone2)
        val top = input.topImpacts
            .sortedByDescending { it.percent }
            .take(TOP_IMPACTS_KEPT)
            .map { it.toTopImpact(input.utcOffsetMinutes) }

        return KeraunicReportData(
            latitude = input.latitude,
            longitude = input.longitude,
            fromSecondsUtc = input.fromSecondsUtc,
            toSecondsUtc = input.toSecondsUtc,
            periodLabel = input.periodLabel,
            nsg = input.nsg,
            nsgLevel = KeraunicReportLogic.nsgLevel(input.nsg),
            nsgRefs = input.nsgRefs,
            zone1 = zone1,
            zone2 = zone2,
            totalImpacts = zone1.totalCount + zone2.totalCount,
            maxKa = maxKa,
            risk = KeraunicReportLogic.assessRisk(zone1.totalCount, maxKa, input.nsg),
            topImpacts = top,
            fallbackImpacts = flashes?.let { fallbackImpacts(it, input) } ?: fallbackImpacts(input),
            monthly1Km = flashes?.let { list -> monthlyFromFlashes(list.filter { it.distKm < 1.0 }, input) }
                ?: monthly(input.monthlyZones) { it.zone1 },
            monthly2Km = flashes?.let { list -> monthlyFromFlashes(list.filter { it.distKm >= 1.0 }, input) }
                ?: monthly(input.monthlyZones) { it.zone2 },
            hourly = flashes?.let(::hourlyFromFlashes) ?: hourly(detail),
            mapImpacts = flashes?.map { it.dot() } ?: mapImpacts(input),
            highlights = flashes?.let { highlightsFromFlashes(it, input) }
                ?: highlights(input, zone1, zone2, maxKa).let { h ->
                    // Detailed report without any flash: said explicitly rather than hiding the key facts
                    val none = detail != null && zone1.totalCount + zone2.totalCount == 0 && h.strongest == null && h.nearest == null
                    if (none) h.copy(noGroundImpact = true) else h
                },
        )
    }

    /** One flash of the detailed report within 2 km, with its distance to the point. */
    private class Flash(
        val peakAmp: Int,
        val distKm: Double,
        val atSecondsUtc: Long,
        val latitude: Double,
        val longitude: Double,
    ) {
        val amplitudeKa: Double get() = abs(peakAmp).toDouble() / 1000.0
        fun dot() = SvgMapBuilder.ImpactDot(latitude, longitude, peakAmp > 0, amplitudeKa)
    }

    /**
     * True when the detailed report lists every flash of its totals with its time (server since November 2026):
     * the report is then computed from these flashes only, and the per-month requests are not needed.
     * An empty list keeps the server statistics (older response, or no flash at all).
     */
    fun hasCompleteActivities(detail: ApiKeraunicDetail?): Boolean {
        if (detail == null) return false
        val total = with(detail) { eventsAt1Km.pos.count + eventsAt1Km.negs.count + eventsAt2Km.pos.count + eventsAt2Km.negs.count }
        return detail.activities.isNotEmpty() && detail.activities.size == total && detail.activities.all { it.atSecondsUtc != null && it.distanceKm != null }
    }

    private fun completeFlashes(input: KeraunicReportInput): List<Flash>? {
        val detail = input.detail?.takeIf(::hasCompleteActivities) ?: return null
        return groundOnly(detail.activities).mapNotNull { act ->
            val dist = act.distanceKm ?: distanceKm(input.latitude, input.longitude, act.latitude, act.longitude)
            if (dist > 2.0) return@mapNotNull null
            Flash(act.pulsePeakAmp, dist, act.atSecondsUtc ?: 0L, act.latitude, act.longitude)
        }
    }

    /**
     * Keraunic reports only count cloud-to-ground strikes ("cg"): intra-cloud flashes ("ic"), often the majority,
     * are left out of every figure (totals, zones, months, hours, intensities, map, key facts, risk).
     * Remove this filter to count every flash again.
     */
    private fun groundOnly(activities: List<ApiKeraunicActivity>): List<ApiKeraunicActivity> =
        activities.filter { it.isCloudToGround }

    // Same statistics as the server's eventsAt1Km / eventsAt2Km, intensities in kA
    private fun flashStats(flashes: List<Flash>): KeraunicZoneStats {
        val neg = flashes.filter { it.peakAmp < 0 }.map { it.amplitudeKa }
        val pos = flashes.filter { it.peakAmp > 0 }.map { it.amplitudeKa }
        return KeraunicZoneStats(
            totalCount = flashes.size,
            negCount = neg.size,
            posCount = pos.size,
            negMaxKa = neg.maxOrNull() ?: 0.0,
            negAvgKa = if (neg.isEmpty()) 0.0 else neg.average(),
            negMedianKa = median(neg),
            posMaxKa = pos.maxOrNull() ?: 0.0,
            posAvgKa = if (pos.isEmpty()) 0.0 else pos.average(),
            posMedianKa = median(pos),
        )
    }

    private fun median(values: List<Double>): Double {
        if (values.isEmpty()) return 0.0
        val sorted = values.sorted()
        val mid = sorted.size / 2
        return if (sorted.size % 2 == 1) sorted[mid] else (sorted[mid - 1] + sorted[mid]) / 2.0
    }

    // Calendar month of each flash in the device time zone
    private fun monthlyFromFlashes(flashes: List<Flash>, input: KeraunicReportInput): List<KeraunicMonthlyData> {
        val byMonth = flashes.groupBy { localTime(it.atSecondsUtc, input.utcOffsetMinutes(it.atSecondsUtc)).month }
        return (1..12).map { m ->
            val stats = flashStats(byMonth[m].orEmpty())
            KeraunicMonthlyData(MONTH_KEYS[m - 1], stats.negCount, stats.posCount, stats.negAvgKa, stats.posAvgKa)
        }
    }

    // Hour of each flash in UTC (the server's hourly graph is shifted by its deltaSeconds)
    private fun hourlyFromFlashes(flashes: List<Flash>): List<KeraunicHourlyData> =
        flashes.groupBy { (it.atSecondsUtc.mod(SECONDS_PER_DAY) / 3600).toInt() }
            .map { (hour, list) ->
                val stats = flashStats(list)
                KeraunicHourlyData(hour, stats.negCount, stats.posCount, stats.negAvgKa, stats.posAvgKa)
            }
            .sortedBy { it.hour }

    // Most intense flash (the nearest one on a tie) and nearest flash, with their time
    private fun highlightsFromFlashes(flashes: List<Flash>, input: KeraunicReportInput): KeraunicHighlights {
        fun Flash.toHighlight() = KeraunicHighlight(amplitudeKa, peakAmp > 0, distKm, time(atSecondsUtc, input))
        val strongest = flashes.sortedWith(compareByDescending<Flash> { abs(it.peakAmp) }.thenBy { it.distKm }).firstOrNull()
        val nearest = flashes.minByOrNull { it.distKm }
        return KeraunicHighlights(strongest?.toHighlight(), nearest?.toHighlight(), noGroundImpact = flashes.isEmpty())
    }

    private fun fallbackImpacts(flashes: List<Flash>, input: KeraunicReportInput): List<KeraunicTopImpact> {
        if (flashes.isEmpty()) return emptyList()
        val nearest = flashes.minBy { it.distKm }
        val strongest = flashes.maxBy { abs(it.peakAmp) }
        val weakest = flashes.minBy { abs(it.peakAmp) }
        return listOf("Le plus proche" to nearest, "Le plus fort" to strongest, "Le plus faible" to weakest)
            .map { (label, flash) ->
                KeraunicTopImpact(
                    externalId = label,
                    dateLabel = "",
                    distKm = flash.distKm,
                    amplitudeKa = flash.amplitudeKa,
                    isPositive = flash.peakAmp > 0,
                    percent = 0.0,
                    atSecondsUtc = flash.atSecondsUtc,
                    localTime = time(flash.atSecondsUtc, input),
                )
            }
    }

    /** Converts a UTC time to the calendar time of the given offset (no time zone library needed). */
    fun localTime(atSecondsUtc: Long, offsetMinutes: Int): KeraunicLocalTime {
        val local = atSecondsUtc + offsetMinutes * 60L
        val days = local.floorDiv(SECONDS_PER_DAY)
        val secondsOfDay = local.mod(SECONDS_PER_DAY)
        // Civil date from days since 1970-01-01 (H. Hinnant's algorithm)
        val z = days + 719_468
        val era = z.floorDiv(146_097L)
        val doe = z - era * 146_097
        val yoe = (doe - doe / 1460 + doe / 36_524 - doe / 146_096) / 365
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val day = (doy - (153 * mp + 2) / 5 + 1).toInt()
        val month = (if (mp < 10) mp + 3 else mp - 9).toInt()
        val year = (yoe + era * 400 + if (month <= 2) 1 else 0).toInt()
        return KeraunicLocalTime(
            year = year,
            month = month,
            day = day,
            hour = (secondsOfDay / 3600).toInt(),
            minute = (secondsOfDay % 3600 / 60).toInt(),
            offsetMinutes = offsetMinutes,
        )
    }

    private fun time(atSecondsUtc: Long?, input: KeraunicReportInput): KeraunicLocalTime? =
        atSecondsUtc?.takeIf { it > 0 }?.let { localTime(it, input.utcOffsetMinutes(it)) }

    private class LocatedImpact(val peakAmp: Int, val distKm: Double, val atSecondsUtc: Long?) {
        val amplitudeKa: Double get() = abs(peakAmp).toDouble() / 1000.0
    }

    /**
     * Most intense and nearest impacts of the period within 2 km. Every impact of the detailed report is a candidate
     * (with its time once the server sends it); an undated impact takes the time of the same top impact of the
     * project (same peak current, same place), otherwise its date is shown as unavailable.
     */
    private fun highlights(
        input: KeraunicReportInput,
        zone1: KeraunicZoneStats,
        zone2: KeraunicZoneStats,
        maxKa: Double,
    ): KeraunicHighlights {
        val tops = input.topImpacts.map { LocatedImpact(it.peakAmp, it.distKm, it.atSecondsUtc) }
        val activities = input.detail?.activities.orEmpty().map { act ->
            val dist = distanceKm(input.latitude, input.longitude, act.latitude, act.longitude)
            val at = act.atSecondsUtc ?: tops.firstOrNull {
                it.peakAmp == act.pulsePeakAmp && abs(it.distKm - dist) < SAME_PLACE_KM
            }?.atSecondsUtc
            LocatedImpact(act.pulsePeakAmp, dist, at)
        }
        val located = (activities.ifEmpty { tops }).filter { it.distKm <= 2.0 }

        fun LocatedImpact.toHighlight(amplitude: Double = amplitudeKa) =
            KeraunicHighlight(amplitude, peakAmp > 0, distKm, time(atSecondsUtc, input))

        // The cover's maximum intensity (zone statistics) is the reference; it is located when an impact matches it
        val strongest = if (maxKa > 0.0) {
            located.filter { abs(it.amplitudeKa - maxKa) < SAME_INTENSITY_KA }
                .sortedWith(compareBy({ it.atSecondsUtc == null }, { it.distKm }))
                .firstOrNull()?.toHighlight(maxKa)
                ?: KeraunicHighlight(maxKa, maxOf(zone1.posMaxKa, zone2.posMaxKa) >= maxKa, null, null)
        } else {
            located.maxByOrNull { it.amplitudeKa }?.toHighlight()
        }
        val nearest = located.minByOrNull { it.distKm }?.toHighlight()
        return KeraunicHighlights(strongest, nearest)
    }

    fun zoneStats(events: ApiKeraunicEvents): KeraunicZoneStats = KeraunicZoneStats(
        totalCount = events.pos.count + events.negs.count,
        negCount = events.negs.count,
        posCount = events.pos.count,
        negMaxKa = events.negs.maxKa(),
        negAvgKa = abs(events.negs.avg) / 1000.0,
        negMedianKa = abs(events.negs.median.toDouble()) / 1000.0,
        posMaxKa = events.pos.maxKa(),
        posAvgKa = abs(events.pos.avg) / 1000.0,
        posMedianKa = abs(events.pos.median.toDouble()) / 1000.0,
    )

    private fun ApiKeraunicEventStats.maxKa() = abs(max.toDouble()) / 1000.0

    private fun KeraunicTopInput.toTopImpact(offset: (Long) -> Int) = KeraunicTopImpact(
        externalId = externalId,
        dateLabel = dateLabel,
        distKm = distKm,
        amplitudeKa = abs(peakAmp).toDouble() / 1000.0,
        isPositive = peakAmp > 0,
        percent = percent,
        sensorCount = sensorCount,
        atSecondsUtc = atSecondsUtc,
        localTime = atSecondsUtc.takeIf { it > 0 }?.let { localTime(it, offset(it)) },
    )

    private fun monthly(
        zones: List<KeraunicMonthlyZone>,
        zone: (KeraunicMonthlyZone) -> ApiKeraunicEvents,
    ): List<KeraunicMonthlyData> = (1..12).map { m ->
        val events = zones.filter { it.month == m }.map(zone)
        val nCount = events.sumOf { it.negs.count }
        val pCount = events.sumOf { it.pos.count }
        KeraunicMonthlyData(
            month = MONTH_KEYS[m - 1],
            negCount = nCount,
            posCount = pCount,
            negAvgKa = if (nCount > 0) events.sumOf { abs(it.negs.avg) * it.negs.count } / nCount / 1000.0 else 0.0,
            posAvgKa = if (pCount > 0) events.sumOf { abs(it.pos.avg) * it.pos.count } / pCount / 1000.0 else 0.0,
        )
    }

    private fun hourly(detail: ApiKeraunicDetail?): List<KeraunicHourlyData> =
        detail?.graph?.hours?.hours.orEmpty().mapNotNull { bucket ->
            val h = bucket.title.toIntOrNull() ?: return@mapNotNull null
            val p = bucket.positif
            val n = bucket.negatif
            if (p.count == 0 && n.count == 0) return@mapNotNull null
            KeraunicHourlyData(
                hour = h,
                negCount = n.count,
                posCount = p.count,
                negAvgKa = abs(n.average) / 1000.0,
                posAvgKa = abs(p.average) / 1000.0,
            )
        }.sortedBy { it.hour }

    // All impacts of the detailed report within 2 km; top impacts when the detailed report has none
    private fun mapImpacts(input: KeraunicReportInput): List<SvgMapBuilder.ImpactDot> {
        val activities = input.detail?.activities.orEmpty()
        if (activities.isEmpty()) {
            return input.topImpacts.map {
                SvgMapBuilder.ImpactDot(it.latitude, it.longitude, it.peakAmp > 0, abs(it.peakAmp).toDouble() / 1000.0)
            }
        }
        val cosLat = cos(input.latitude * PI / 180.0)
        return activities.mapNotNull { act ->
            val dlat = (act.latitude - input.latitude) * M_PER_DEG
            val dlon = (act.longitude - input.longitude) * M_PER_DEG * cosLat
            if (sqrt(dlat * dlat + dlon * dlon) > MAP_RADIUS_M) return@mapNotNull null
            SvgMapBuilder.ImpactDot(act.latitude, act.longitude, act.pulsePeakAmp > 0, abs(act.pulsePeakAmp).toDouble() / 1000.0)
        }
    }

    // Labels are the French keys translated by KeraunicReportText.fallbackImpactLabel()
    private fun fallbackImpacts(input: KeraunicReportInput): List<KeraunicTopImpact> {
        val withDist = input.detail?.activities.orEmpty()
            .map { it to distanceKm(input.latitude, input.longitude, it.latitude, it.longitude) }
            .filter { it.second <= 2.0 }
        if (withDist.isEmpty()) return emptyList()
        val nearest = withDist.minBy { it.second }
        val strongest = withDist.maxBy { abs(it.first.pulsePeakAmp) }
        val weakest = withDist.minBy { abs(it.first.pulsePeakAmp) }
        return listOf("Le plus proche" to nearest, "Le plus fort" to strongest, "Le plus faible" to weakest)
            .map { (label, pair) ->
                KeraunicTopImpact(
                    externalId = label,
                    dateLabel = "",
                    distKm = pair.second,
                    amplitudeKa = abs(pair.first.pulsePeakAmp).toDouble() / 1000.0,
                    isPositive = pair.first.pulsePeakAmp > 0,
                    percent = 0.0,
                    atSecondsUtc = pair.first.atSecondsUtc,
                    localTime = time(pair.first.atSecondsUtc, input),
                )
            }
    }

    fun distanceKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        val dLat = (lat2 - lat1) * PI / 180.0
        val dLon = (lon2 - lon1) * PI / 180.0
        val a = sin(dLat / 2).pow(2) + cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2).pow(2)
        return 2 * EARTH_RADIUS_KM * asin(min(1.0, sqrt(a)))
    }
}
